import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Prompt } from "react-router-dom";
import {
  Backdrop,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Checkbox,
  Chip,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import { Autocomplete } from "@material-ui/lab";
import TabHeader from "src/components/TabHeader/TabHeader";
import CommonLoader from "src/components/Loader/CommonLoader";
import CommonButton from "src/components/CommonButton/CommonButton";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import TransitionDialog from "src/components/AlertDialog/TransitionDialog";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { useAppSelector } from "src/app-redux/hooks";
import { NodCommercialRoute } from "../../nod-commercial.routes";
import { useStyles } from "../TerminalCharges/components/style";
import { numberFormatting } from "src/common/utils/helpers";
import {
  selectLoader,
  selectPaymentTypes,
  selectWeightScanTypes,
  addPaymentAction,
  removeAllPayments,
} from "src/app-redux/commercial/commercialSlice";
import {
  getPaymentTypesDropdownAction,
  postTerminalChargesWholeRecordAction,
} from "src/app-redux/commercial/actions/commercialActions";
import {
  selectAllTaxTypeRowsList,
  selectAllAgent,
} from "src/app-redux/configuration/configurationSlice";
import {
  getAllAgents,
  getAllPartName,
  getAllTaxTypes,
} from "src/app-redux/configuration/actions/configurationActions";

export function ImporterWiseCharges(): React.ReactElement {
  const classes = useStyles();
  const dispatch = useDispatch();
  const moduleName = NodCommercialRoute.title;
  const pageHeading = "Importer Wise Invoice";
  const loading = useAppSelector(selectLoader);
  const allTaxTypeRows: any[] = useAppSelector(selectAllTaxTypeRowsList) as any;
  const allAgents: any[] = useAppSelector(selectAllAgent) as any;
  const paymentTypes: any[] = useAppSelector(selectPaymentTypes);
  const weightScanTypesRecord: any[] = useAppSelector(selectWeightScanTypes);

  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";

  const [loader2, setLoader2] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [dialogContent, setDialogContent] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);

  const [pendingContainerList, setPendingContainerList] = useState<any[]>([]);
  const [selectedContainers, setSelectedContainers] = useState<any[]>([]);
  const [importerOptions, setImporterOptions] = useState<Array<{ id: any; name: string }>>([]);
  const [importerId, setImporterId] = useState<string>("");
  const [partyId, setPartyId] = useState<string>(""); // selected importer name (display)
  const [agentId, setAgentId] = useState<string>("");
  const containerFieldWrapRef = useRef<HTMLDivElement | null>(null);

  const [filterGridRecord, setFilterGridRecord] = useState<any[]>([]);
  const [apiSubmitRecordData, setApiSubmitRecordData] = useState<any[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [tax, setTax] = useState<any>({ taxType: "", taxRate: 0 });
  const [discount, setDiscount] = useState<number>(0);
  const [remarks, setRemarks] = useState<string>("");
  const [currency, setCurrency] = useState<string>("PKR");

  const columns = useMemo(
    () => [
      { accessorKey: "paymentType", header: "Type of Payment", flex: 1 },
      { accessorKey: "paymentQuantity", header: "Quantity", flex: 1 },
      { accessorKey: "charges", header: "Charges", flex: 1 },
    ],
    []
  );

  // In Gate-In, importer is captured as `importerName` and also surfaces as `importer`
  // in some downstream payloads. We keep this page aligned to that string field.
  const importerDisplayName = useMemo(() => (partyId ? partyId : "-"), [partyId]);

  const normalizeImporterContainerRows = (raw: any): any[] => {
    const list = Array.isArray(raw)
      ? raw
      : raw?.containersList ||
        raw?.list ||
        raw?.containerList ||
        raw?.containers ||
        raw?.data ||
        [];
    return (list || []).map((row: any, idx: number) => {
      const containerNumber =
        row?.containerNumber ?? row?.containerNo ?? row?.container ?? row?.name ?? "";
      return {
        ...row,
        id: row?.id ?? row?.gatePassId ?? `${containerNumber}-${idx}`,
        containerNumber,
      };
    });
  };

  const fetchImporterDropdown = async () => {
    setImporterOptions([]);
    try {
      setLoader2(true);
      const results = await fetchWrapper.get(
        "get_all-supplier-details?getActiveSuppliersOnly=true"
      );
      if (results?.isSuccess) {
        const rows: any[] = Array.isArray(results?.value) ? results.value : [];
        const options = rows
          .filter((r: any) => String(r?.supplierType || "").trim() === "Importer")
          .map((r: any, idx: number) => {
            const name = String(r?.supplierName ?? r?.importerName ?? r?.name ?? "").trim();
            return { id: r?.id ?? name ?? idx, name };
          })
          .filter((o: any) => o.name);
        setImporterOptions(options);
      } else {
        dispatch(
          addNotificationAction({
            message: results?.message || "Could not load importers.",
            options: { variant: "error" },
          })
        );
      }
    } catch {
      dispatch(
        addNotificationAction({
          message: "Could not load importers.",
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader2(false);
    }
  };

  // NOTE: Importer-wise invoice uses `get-all-containers-by-importer-id/{importerId}` on selection,
  // so we don't need to prefetch released VIRs here.

  const fetchContainersByImporterId = async (id: string) => {
    setPendingContainerList([]);
    setSelectedContainers([]);
    if (!id) return;
    try {
      setLoader2(true);
      const results = await fetchWrapper.get(
        `get-all-containers-by-importer-id/${encodeURIComponent(id)}`
      );
      if (results?.isSuccess) {
        const rows = normalizeImporterContainerRows(results.value);
        setPendingContainerList(rows);
        // Start with no selection; user explicitly links containers via multi-select.
        setSelectedContainers([]);
      } else {
        setPendingContainerList([]);
        setSelectedContainers([]);
        dispatch(
          addNotificationAction({
            message: results?.message || "Could not load containers for this importer.",
            options: { variant: "error" },
          })
        );
      }
    } catch {
      setPendingContainerList([]);
      setSelectedContainers([]);
      dispatch(
        addNotificationAction({
          message: "Could not load containers for this importer.",
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader2(false);
    }
  };

  const containersSummary = useMemo(() => {
    const list = pendingContainerList || [];
    const totalContainers = list.length;

    const sizeCounts = new Map<number, number>();
    list.forEach((row: any) => {
      const sz = row?.containerSize ?? row?.size ?? row?.container_size;
      if (sz === undefined || sz === null || sz === "") return;
      const n = Number(sz);
      if (Number.isNaN(n)) return;
      sizeCounts.set(n, (sizeCounts.get(n) || 0) + 1);
    });
    const categoryLabel =
      sizeCounts.size === 0
        ? "-"
        : Array.from(sizeCounts.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([size, count]) => `${count}x${size}`)
            .join(", ");

    const vehicleKeys = list
      .map((r: any) => r?.vehicleNumber ?? r?.vehicleNo ?? r?.regNo ?? r?.registrationNumber)
      .filter((v: any) => v != null && String(v).trim() !== "");
    const totalVehiclesUnique = new Set(vehicleKeys.map((v: any) => String(v).trim())).size;
    return { totalContainers, categoryLabel, totalVehiclesUnique };
  }, [pendingContainerList]);

  const getPaymentTypeIdByName = (name: string): number | null => {
    const match = (paymentTypes || []).find(
      (pt: any) =>
        String(pt?.name || "")
          .trim()
          .toLowerCase() === name.trim().toLowerCase()
    );
    const id = match?.id ?? match?.paymentTypeId;
    const num = Number(id);
    return Number.isFinite(num) && num > 0 ? num : null;
  };

  const getPaymentTypeAmountById = async (id: number): Promise<number> => {
    const res = await fetchWrapper.get(`get-payment-type-by-id/${id}`);
    if (!res?.isSuccess) throw new Error(res?.message || "Failed to load payment type details");
    const amount = Number(res?.value?.amount ?? 0);
    return Number.isFinite(amount) ? amount : 0;
  };

  const getExaminationTotalFromContainers = (containers: any[]): number => {
    // `get-all-containers-by-importer-id/{importerId}` returns `examinationCharges` per container.
    // Examination Payment should reflect sum of those charges.
    const total = (containers || []).reduce((sum: number, r: any) => {
      const n = Number(r?.examinationCharges ?? 0);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
    return Math.round(total);
  };

  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const STORAGE_FREE_DAYS = 8;

  const getStorageStartDate = (row: any): Date | null => {
    // Follow TerminalCharges logic as closely as possible:
    // - CY: use gateInDate (fallback createdDateTime)
    // - CFS: use destuffingDate
    // - Otherwise: best-effort fallback to gateInDate/createdDateTime/destuffingDate
    const containerType = String(row?.containerType ?? "")
      .trim()
      .toUpperCase();

    let raw: any = null;
    const gateInCandidate =
      row?.gateInDate ??
      row?.gateinDate ??
      row?.gateInDatetime ??
      row?.gateInDateTime ??
      row?.gateInTime ??
      row?.gateIn ??
      row?.gate_in_date ??
      row?.gate_in_datetime;

    const createdCandidate =
      row?.createdDateTime ??
      row?.createdDatetime ??
      row?.createdDate ??
      row?.createdOn ??
      row?.createdAt ??
      row?.creationDateTime;

    const destuffCandidate =
      row?.destuffingDate ??
      row?.destuffDate ??
      row?.deStuffingDate ??
      row?.destuffingDatetime ??
      row?.destuffingDateTime;

    if (containerType === "CY") raw = gateInCandidate ?? createdCandidate;
    else if (containerType === "CFS") raw = destuffCandidate ?? gateInCandidate ?? createdCandidate;
    else raw = gateInCandidate ?? createdCandidate ?? destuffCandidate;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const getChargeableStorageDays = (row: any): number => {
    // Some APIs already return storage days; if present, trust it.
    const apiDaysRaw =
      row?.storageDays ??
      row?.noOfStorageDays ??
      row?.noOfStorageDay ??
      row?.noOfDaysInYard ??
      row?.yardDays ??
      row?.storage_day ??
      row?.storageDay;
    const apiDays = Number(apiDaysRaw);
    const totalStorageDaysFromApi =
      Number.isFinite(apiDays) && apiDays >= 0 ? Math.floor(apiDays) : null;

    const totalStorageDays =
      totalStorageDaysFromApi !== null
        ? totalStorageDaysFromApi
        : (() => {
            const start = getStorageStartDate(row);
            if (!start) return 0;
            const today = new Date();
            const diffDays = Math.floor((today.getTime() - start.getTime()) / MS_PER_DAY);
            return Math.max(0, diffDays);
          })();

    return Math.max(0, totalStorageDays - STORAGE_FREE_DAYS);
  };

  const applyCharges = async () => {
    if (!importerId || selectedContainers.length === 0) {
      dispatch(
        addNotificationAction({
          message: "Please select Importer and at least one container first.",
          options: { variant: "error" },
        })
      );
      return;
    }
    if (!agentId) {
      dispatch(
        addNotificationAction({
          message: "Please select Agent Name first.",
          options: { variant: "error" },
        })
      );
      return;
    }

    dispatch(removeAllPayments());

    const containers = selectedContainers;
    const totalContainers = containers.length;
    const countBySize = containers.reduce((acc: Record<string, number>, row: any) => {
      const key = String(row?.containerSize ?? row?.size ?? "").trim();
      if (!key) return acc;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
    const qty20 = countBySize["20"] || 0;
    const qty40 = countBySize["40"] || 0;
    const qty45 = countBySize["45"] || 0;

    const storageDaysBySize = containers.reduce(
      (acc: Record<"20" | "40" | "45", number>, row: any) => {
        const size = String(row?.containerSize ?? row?.size ?? "").trim() as "20" | "40" | "45";
        if (size !== "20" && size !== "40" && size !== "45") return acc;
        acc[size] += getChargeableStorageDays(row);
        return acc;
      },
      { "20": 0, "40": 0, "45": 0 }
    );

    const vehicleKeys = containers
      .map((r: any) => r?.vehicleNumber ?? r?.vehicleNo ?? r?.regNo ?? r?.registrationNumber)
      .filter((v: any) => v != null && String(v).trim() !== "")
      .map((v: any) => String(v).trim());
    const totalVehiclesUnique = new Set(vehicleKeys).size;

    const rules: Array<{ name: string; quantity: number }> = [
      { name: "Surcharge Payment", quantity: totalContainers },
      { name: "Loading 20ft Payment", quantity: qty20 },
      { name: "Loading 40ft Payment", quantity: qty40 },
      { name: "Loading 45ft Payment", quantity: qty45 },
      // Storage: first 8 days free. Then charge per-day forever, size-wise.
      // Quantity is TOTAL chargeable days across selected containers for that size.
      { name: "Storage Payment 20ft", quantity: storageDaysBySize["20"] },
      { name: "Storage Payment 40ft", quantity: storageDaysBySize["40"] },
      { name: "Storage Payment 45ft", quantity: storageDaysBySize["45"] },
      { name: "Vehicle Exit Payment", quantity: totalVehiclesUnique },
      { name: "Vehicle Local Payment", quantity: totalVehiclesUnique },
    ].filter((r) => r.quantity > 0);

    try {
      setLoader2(true);

      for (const rule of rules) {
        const paymentTypeId = getPaymentTypeIdByName(rule.name);
        if (!paymentTypeId) {
          dispatch(
            addNotificationAction({
              message: `Payment type not found in configuration: ${rule.name}`,
              options: { variant: "error" },
            })
          );
          continue;
        }
        const amountPerUnit = await getPaymentTypeAmountById(paymentTypeId);
        dispatch(
          addPaymentAction({
            paymentTypeId,
            paymentType: rule.name,
            paymentQuantity: rule.quantity,
            charges: amountPerUnit,
            currency,
            discount: 0,
            discountedCharges: amountPerUnit,
          })
        );
      }

      // Examination Payment comes from `examinationCharges` per container
      const examTotal = getExaminationTotalFromContainers(containers);
      if (examTotal > 0) {
        // Not configuration-driven: it is computed from API response per container.
        dispatch(
          addPaymentAction({
            paymentTypeId: 0,
            paymentType: "Examination Payment",
            paymentQuantity: 1,
            charges: examTotal,
            currency,
            discount: 0,
            discountedCharges: examTotal,
          })
        );
      }

      dispatch(
        addNotificationAction({
          message: "Charges applied",
          options: { variant: "success" },
        })
      );
      setIsDirty(true);
    } catch (e: any) {
      dispatch(
        addNotificationAction({
          message: e?.message || "Failed to apply charges",
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader2(false);
    }
  };

  const calculateTax = () => {
    const amt = Number(totalAmount) || 0;
    const taxRate = Number(tax?.taxRate) || 0;
    return Math.round((amt * taxRate) / 100);
  };

  const calculateNetAmount = () => {
    const amt = Number(totalAmount) || 0;
    const taxAmt = calculateTax();
    const disc = Number(discount) || 0;
    return Math.round(amt + taxAmt - disc);
  };

  const recordSaveHandler = () => {
    let grossTotal = 0;
    let discountTotal = 0;

    const newData = (weightScanTypesRecord || []).map((item: any) => {
      const quantity = item.paymentQuantity || 1;
      const discountPerUnit = item.discount || 0;
      const totalDiscount = discountPerUnit * quantity;
      const totalCharges = (item.charges || 0) * quantity;

      grossTotal += totalCharges;
      discountTotal += totalDiscount;

      return {
        ...item,
        charges: `${item.currency || "PKR"} ${numberFormatting(totalCharges)} `,
      };
    });

    setTotalAmount(grossTotal);
    setDiscount(discountTotal);
    setFilterGridRecord(newData);

    const newData2 = (weightScanTypesRecord || []).map((item: any) => {
      const quantity = item.paymentQuantity || 1;
      return {
        ...item,
        charges: Math.round((item.charges || 0) * quantity),
        discount: Math.round((item.discount || 0) * quantity),
      };
    });
    setApiSubmitRecordData(newData2);
  };

  const resetWholePage = () => {
    setImporterId("");
    setPartyId("");
    setAgentId("");
    setPendingContainerList([]);
    setSelectedContainers([]);
    setFilterGridRecord([]);
    setApiSubmitRecordData([]);
    setTotalAmount(0);
    setTax({ taxType: "", taxRate: 0 });
    setDiscount(0);
    setRemarks("");
    setCurrency("PKR");
    setIsDirty(false);
    dispatch(removeAllPayments());
  };

  const submitCompletePage = async () => {
    if (!agentId) {
      dispatch(
        addNotificationAction({
          message: "Please select Agent Name first.",
          options: { variant: "error" },
        })
      );
      return;
    }
    if (!apiSubmitRecordData.length) {
      dispatch(
        addNotificationAction({
          message: "Please add some Payment record!",
          options: { variant: "error" },
        })
      );
      return;
    }

    let data = "";
    apiSubmitRecordData.forEach((item: any) => {
      data = data.concat(
        `${item.paymentType}: ${item.currency || currency} ${numberFormatting(item.charges)} #`
      );
    });
    data = data.concat(` Total Charges: ${currency} ${numberFormatting(calculateNetAmount())}`);
    setDialogContent(data);
    setIsOpen(true);
  };

  const handleConfirmDialog = async () => {
    setIsOpen(false);
    if (!importerId) {
      dispatch(
        addNotificationAction({
          message: "Please select Importer first.",
          options: { variant: "error" },
        })
      );
      return;
    }
    if (!agentId) {
      dispatch(
        addNotificationAction({
          message: "Please select Agent Name first.",
          options: { variant: "error" },
        })
      );
      return;
    }
    if (!apiSubmitRecordData.length) {
      dispatch(
        addNotificationAction({
          message: "Please add some Payment record!",
          options: { variant: "error" },
        })
      );
      return;
    }

    const importerName = partyId.trim();
    const importerIdValue = (() => {
      const n = Number(importerId);
      return Number.isFinite(n) && n > 0 ? n : importerId;
    })();

    const list = selectedContainers || [];
    const rowsWithGatePass = list.filter((r: any) => {
      const id = r?.gatePassId ?? r?.gatePassID ?? r?.gatePassid;
      return id !== undefined && id !== null && String(id).trim() !== "";
    });
    const gatePassIds = rowsWithGatePass
      .map((r: any) => String(r?.gatePassId ?? r?.gatePassID ?? r?.gatePassid).trim())
      .join(",");
    const vehicleNumber = [
      ...new Set(
        rowsWithGatePass
          .map((r: any) =>
            String(
              r?.vehicleNumber ?? r?.vehicleNo ?? r?.regNo ?? r?.registrationNumber ?? ""
            ).trim()
          )
          .filter((v: string) => v !== "")
      ),
    ].join(",");

    const gatePassId = rowsWithGatePass[0]
      ? String(
          rowsWithGatePass[0]?.gatePassId ??
            rowsWithGatePass[0]?.gatePassID ??
            rowsWithGatePass[0]?.gatePassid
        ).trim()
      : "";

    const record = {
      gatePassId,
      importerId: importerIdValue,
      importerName,
      // backend `/create-payment` historically uses `partyId` for the selected party
      partyId: importerIdValue,
      agentId: agentId
        ? (() => {
            const n = Number(agentId);
            return Number.isFinite(n) && n > 0 ? n : agentId;
          })()
        : undefined,
      gatePassIds,
      vehicleNumber,
      gatePassPaymentDetail: apiSubmitRecordData,
      remarks,
      taxId: tax?.id,
      discount: Number(discount) || 0,
      taxAmount: calculateTax(),
      taxRate: Number(tax?.taxRate) || 0,
      netAmount: calculateNetAmount(),
      totalAmount,
      currency,
    };

    try {
      setLoader2(true);
      const action: any = await dispatch(postTerminalChargesWholeRecordAction(record) as any);
      const status = action?.meta?.requestStatus;
      if (status !== "fulfilled") {
        throw new Error(
          action?.error?.message || action?.payload?.message || "Failed to create payment"
        );
      }

      dispatch(
        addNotificationAction({
          message: "Payment created",
          options: { variant: "success" },
        })
      );
      setDialogContent("");
      resetWholePage();
    } catch (e: any) {
      dispatch(
        addNotificationAction({
          message: e?.message || "Failed to create payment",
          options: { variant: "error" },
        })
      );
    } finally {
      setLoader2(false);
    }
  };

  const exportHandler = async (row: any) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );
    return (row || []).map((data: any) => ({
      "Type of Payment": data?.paymentType,
      Quantity: data?.paymentQuantity,
      Charges: data?.charges,
    }));
  };

  useEffect(() => {
    dispatch(getAllPartName(true));
    dispatch(getAllAgents(true) as any);
    dispatch(removeAllPayments());
    dispatch(getPaymentTypesDropdownAction());
    dispatch(getAllTaxTypes(1));
    void fetchImporterDropdown();
    return () => {
      dispatch(removeAllPayments());
    };
  }, []);

  useEffect(() => {
    recordSaveHandler();
  }, [weightScanTypesRecord]);

  useEffect(() => {
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);

  return (
    <div>
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <TabHeader module={moduleName} heading={pageHeading} link={NodCommercialRoute.path} />
      <Backdrop className={classes.backdrop} open={loading || loader2}>
        <CommonLoader />
      </Backdrop>
      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetWholePage()}>
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  onClick={() => submitCompletePage()}
                >
                  Save
                </CommonButton>
              </Box>
            </Grid>
          </Grid>

          <Box style={{ marginBottom: "10px" }}>
            <Divider />
          </Box>

          <Grid spacing={2} container direction="row">
            <Grid item xs={12} md={6}>
              <Grid spacing={2} container direction="row">
                <Grid item xs={12} md={3}>
                  <Box>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      label="Importer"
                      placeholder="Select Importer Name"
                      name="PartyId"
                      optionsType={importerOptions}
                      getOptionLabel={(option: any) => option.name || ""}
                      value={
                        importerOptions.find((p: any) => String(p.name) === String(partyId)) || null
                      }
                      onChange={(option: any) => {
                        if (!option) {
                          setImporterId("");
                          setPartyId("");
                          setAgentId("");
                          setPendingContainerList([]);
                          setSelectedContainers([]);
                          setIsDirty(true);
                          return;
                        }
                        const nextId = String(option?.id ?? "").trim();
                        const nextName = String(option?.name ?? "").trim();
                        setImporterId(nextId);
                        setPartyId(nextName);
                        setIsDirty(true);
                        void fetchContainersByImporterId(nextId);
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      label="Agent Name"
                      placeholder="Select Agent Name"
                      name="agentId"
                      required
                      optionsType={allAgents || []}
                      getOptionLabel={(option: any) => option?.name || ""}
                      value={
                        (allAgents || []).find((a: any) => String(a?.id) === String(agentId)) ||
                        null
                      }
                      onChange={(option: any) => {
                        if (!option) {
                          setAgentId("");
                          setIsDirty(true);
                          return;
                        }
                        setAgentId(String(option?.id ?? "").trim());
                        setIsDirty(true);
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box width="100%">
                    <div ref={containerFieldWrapRef} style={{ width: "100%" }}>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={pendingContainerList || []}
                        value={selectedContainers}
                        onChange={(_e, nextValue) => {
                          setSelectedContainers(nextValue || []);
                          setIsDirty(true);
                        }}
                        getOptionLabel={(option: any) => String(option?.containerNumber || "")}
                        renderOption={(option: any, { selected }) => {
                          const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
                          const checkedIcon = <CheckBoxIcon fontSize="small" />;
                          return (
                            <React.Fragment>
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={selected}
                              />
                              {option?.containerNumber || "-"}
                            </React.Fragment>
                          );
                        }}
                        renderTags={(value: any[], getTagProps: any) => {
                          const normalized = (value || [])
                            .map((v: any) => String(v?.containerNumber || v || "").trim())
                            .filter(Boolean);
                          if (!normalized.length) return null;

                          const first = normalized[0];
                          const remaining = normalized.slice(1);
                          const remainingCount = remaining.length;

                          return (
                            <>
                              <Chip
                                size="small"
                                label={first}
                                {...getTagProps({ index: 0 })}
                                style={{ maxWidth: 180 }}
                              />
                              {remainingCount > 0 && (
                                <Tooltip
                                  placement="top"
                                  title={
                                    <div
                                      style={{
                                        whiteSpace: "pre-line",
                                        fontSize: 14,
                                        lineHeight: 1.6,
                                        padding: "8px 10px",
                                        minWidth: 220,
                                        maxWidth: 360,
                                        maxHeight: 240,
                                        overflowY: "auto",
                                      }}
                                    >
                                      {remaining.join("\n")}
                                    </div>
                                  }
                                >
                                  <Chip
                                    size="small"
                                    label={`+${remainingCount} more`}
                                    style={{ marginLeft: 6 }}
                                  />
                                </Tooltip>
                              )}
                            </>
                          );
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className="customTextField"
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Linked Container"
                            placeholder="Select container(s)"
                            InputLabelProps={{ shrink: true }}
                            disabled={!partyId || pendingContainerList.length === 0}
                          />
                        )}
                      />
                    </div>
                  </Box>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Box
                    textAlign="inherit"
                    mt={1}
                    mb={1}
                    className="terminalChargesSearchBtns headerButtonsConfig"
                    style={{ float: "right" }}
                  >
                    <CommonButton
                      cssClass="customSubmitBtn"
                      isGreen
                      disable={!partyId || selectedContainers.length === 0}
                      onClick={() => void applyCharges()}
                    >
                      Apply Charges
                    </CommonButton>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                style={{
                  padding: "20px",
                  backgroundColor: "rgb(0 0 0 / 7%)",
                  border: "none",
                }}
              >
                <Grid spacing={2} container direction="row" justifyContent="space-between">
                  <Grid
                    item
                    xs={12}
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Grid item xs={5} sm={4} md={5}>
                      <Typography className={classes.readOnyTitle}>Importer:</Typography>
                    </Grid>
                    <Grid item xs={7} sm={8} md={7}>
                      <Typography align="right">{importerDisplayName}</Typography>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Grid item xs={5} sm={4} md={5}>
                      <Typography className={classes.readOnyTitle}>Total Containers:</Typography>
                    </Grid>
                    <Grid item xs={7} sm={8} md={7}>
                      <Typography align="right">
                        {partyId ? containersSummary.totalContainers : "-"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Grid item xs={5} sm={4} md={5}>
                      <Typography className={classes.readOnyTitle}>Category:</Typography>
                    </Grid>
                    <Grid item xs={7} sm={8} md={7}>
                      <Typography align="right" style={{ wordBreak: "break-word" }}>
                        {partyId ? containersSummary.categoryLabel : "-"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Grid item xs={5} sm={4} md={5}>
                      <Typography className={classes.readOnyTitle}>Total Vehicles:</Typography>
                    </Grid>
                    <Grid item xs={7} sm={8} md={7}>
                      <Typography align="right">
                        {partyId ? containersSummary.totalVehiclesUnique : "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

          <div className={classes.grid}>
            <CommonMaterialGrid
              rows={filterGridRecord}
              columns={columns}
              defaultPerPageRecord={50}
              height={450}
              isExport={isExport}
              exportHandler={exportHandler}
              filename={`Importer_Wise_Invoice_${Date.now()}`}
            />
          </div>

          <Grid spacing={2} container direction="row">
            <Grid item xs={4} md={4} className={classes.remarksDiv}>
              <Box>
                <TextField
                  className="customTextField"
                  size="small"
                  fullWidth
                  multiline
                  rows={1}
                  rowsMax={5}
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  label="Remarks"
                  name="remarks"
                  placeholder="Remarks"
                  value={remarks}
                  onChange={(e) => {
                    setRemarks(e.target.value);
                    setIsDirty(true);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={4} md={4} className={classes.remarksDiv}>
              <Box>
                <CommonAutoCompleteField
                  cssClassName="customTextField"
                  label="Tax Type (%)"
                  placeholder="Select Tax Type"
                  name="taxTypeId"
                  optionsType={allTaxTypeRows}
                  getOptionLabel={(option: any) =>
                    `${option.taxType.toString()} - ${option?.taxRate.toString()}%`
                  }
                  onChange={(row: any) => {
                    setTax(row || { taxType: "", taxRate: 0 });
                    setIsDirty(true);
                  }}
                  value={allTaxTypeRows.find((st: any) => st.id == tax?.id) || null}
                />
              </Box>
            </Grid>
            <Grid item xs={4} md={4} className={classes.remarksDiv}>
              <Box>
                <TextField
                  className="customTextField"
                  type="number"
                  InputProps={{ inputProps: { min: 0, max: Number(totalAmount) } }}
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  label="Discount"
                  name="discount"
                  placeholder="Discount"
                  value={discount}
                  onChange={(e) => {
                    setDiscount(Number(e.target.value) || 0);
                    setIsDirty(true);
                  }}
                  onPaste={(e: any) => {
                    if (/[^0-9]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Grid
            item
            md={12}
            container
            className={`${classes.totalDiv} ${"commercialChargeTotalDiv"}`}
          >
            <Grid item md={3}>
              <Typography variant="h6" className={classes.subHeadings}>
                Total <span style={{ fontSize: "10px" }}>({currency})</span>:{"  "}
                <span className={classes.totalVal}>{`${numberFormatting(
                  Number(totalAmount)
                )} `}</span>
              </Typography>
            </Grid>
            <Grid item md={3}>
              <Typography variant="h6" className={classes.subHeadings}>
                STax Amount <span style={{ fontSize: "10px" }}>({currency})</span>:{"  "}
                <span className={classes.totalVal}>{` ${numberFormatting(calculateTax())} `}</span>
              </Typography>
            </Grid>
            <Grid item md={3}>
              <Typography variant="h6" className={classes.subHeadings}>
                Discount <span style={{ fontSize: "10px" }}>({currency})</span>:{"  "}
                <span className={classes.totalVal}>{` ${numberFormatting(
                  Number(discount) || 0
                )} `}</span>
              </Typography>
            </Grid>
            <Grid item md={3}>
              <Typography variant="h6" className={classes.subHeadings}>
                Net Amount <span style={{ fontSize: "10px" }}>({currency})</span>:{"  "}
                <span className={classes.totalVal}>{` ${numberFormatting(
                  calculateNetAmount()
                )} `}</span>
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TransitionDialog
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        heading={"Are you sure you want to save the following charges?"}
        handleConfirm={handleConfirmDialog}
      >
        {dialogContent &&
          dialogContent?.split("#").map((data: any, index: any) => (
            <Box key={index}>
              <p style={{ lineHeight: "0.7" }}>{data}</p>
            </Box>
          ))}
      </TransitionDialog>
    </div>
  );
}
